'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useAppStore } from '@/store/app';
import { translate } from '@/lib/i18n';
import OverviewCards from '@/components/OverviewCards';
import LiquidationsView from '@/components/LiquidationsView';
import MarketTable from '@/components/MarketTable';
import LiqTicker from '@/components/LiqTicker';
import PullToRefresh from '@/components/PullToRefresh';

export default function MarketsPage() {
  const { language } = useAppStore();
  const t = translate(language);
  const zh = language !== 'en';

  // UI02/FIX08: rankings & liquidation are mutually exclusive tabs (default rankings)
  const [marketTab, setMarketTab] = useState<'rank' | 'liq'>('rank');
  // D14: 覆盖范围与更新时间帮助入口
  const [showCoverHelp, setShowCoverHelp] = useState(false);
  // v0.8: 下拉刷新 tick —— 传给 MarketTable 触发 refetch
  const [refreshTick, setRefreshTick] = useState(0);

  return (
    <PullToRefresh
      onRefresh={async () => {
        setRefreshTick((v) => v + 1);
      }}
      refreshingLabel={zh ? '行情刷新中…' : 'Refreshing…'}
      pullLabel={zh ? '下拉刷新行情' : 'Pull to refresh'}
    >
    <div>
      <div className="mb-4 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="flex items-baseline gap-2 text-xl font-bold sm:text-2xl">
            <span className="font-black tracking-tight">AiLumix</span>
            <span className="hidden text-sm font-normal text-gf-muted sm:inline">
              {zh ? '波动可见，决策有据' : 'See Volatility, Decide with Confidence'}
            </span>
          </h1>
          <div className="mt-1 text-xs text-gf-muted sm:hidden">
            {zh ? '波动可见，决策有据' : 'See Volatility, Decide with Confidence'}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
          <Link
            href="/discover"
            className="whitespace-nowrap rounded-md border border-gf-border bg-gf-surface2 px-2.5 py-1.5 text-xs hover:border-gf-accent hover:text-gf-accent sm:px-3"
          >
            {zh ? '排行榜' : 'Rankings'} →
          </Link>
          <Link
            href="/screener"
            className="whitespace-nowrap rounded-md border border-gf-accent bg-gf-accent/10 px-2.5 py-1.5 text-xs text-gf-accent hover:bg-gf-accent/20 sm:px-3"
          >
            {zh ? '筛选器' : 'Screener'}
          </Link>
        </div>
      </div>
      <div className="mb-3 flex items-center gap-1.5 text-xs text-gf-muted">
        <span>{zh ? '已收录' : 'Covered'} · Binance + OKX + Gate</span>
        <button
          type="button"
          onClick={() => setShowCoverHelp((v) => !v)}
          className="rounded-full border border-gf-border bg-gf-surface2 px-2 py-0.5 text-[10px] text-gf-muted hover:border-gf-accent hover:text-gf-accent"
        >
          {zh ? '数据详情' : 'Data help'}
        </button>
      </div>
      {showCoverHelp && (
        <div className="mb-3 rounded-lg border border-gf-border/60 bg-gf-surface2/50 p-3 text-xs leading-relaxed text-gf-muted">
          {zh
            ? '覆盖：永续合约取 Binance / OKX / Gate 三所共同市场；现货另含 Bitget 四所行情。行情约每 10 秒更新，链上与事件数据独立采集。资产级市值按基础资产去重，市场级合约榜分交易所展示。'
            : 'Coverage: perpetuals from Binance / OKX / Gate; spot adds Bitget (4 exchanges). Prices refresh ~10s; on-chain & event data are collected separately. Asset-level caps are deduped by base asset; market-level contract rankings show per exchange.'}
        </div>
      )}

      {/* Four market summaries */}
      <div className="card">
        <OverviewCards />
      </div>

      {/* UI02: rankings / liquidation — mutually exclusive tabs, state preserved while hidden */}
      <div className="mt-3 flex gap-1 rounded-lg border border-gf-border bg-gf-surface2 p-1">
        <button
          onClick={() => setMarketTab('rank')}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            marketTab === 'rank' ? 'bg-gf-accent/15 text-gf-accent' : 'text-gf-muted hover:text-gf-text'
          }`}
        >
          {zh ? '币种排行' : 'Rankings'}
        </button>
        <button
          onClick={() => setMarketTab('liq')}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            marketTab === 'liq' ? 'bg-gf-accent/15 text-gf-accent' : 'text-gf-muted hover:text-gf-text'
          }`}
        >
          {zh ? '爆仓监控' : 'Liquidations'}
        </button>
      </div>

      {/* Rankings body */}
      <div className={marketTab === 'rank' ? '' : 'hidden'}>
        <MarketTable refreshTick={refreshTick} />
      </div>

      {/* Liquidation body (ticker lives inside this tab only) */}
      <div className={marketTab === 'liq' ? '' : 'hidden'}>
        <LiqTicker language={language} />
        <div className="card mt-3">
          <div className="mb-3 text-sm font-semibold text-gf-muted">
            {zh ? '爆仓数据' : 'Liquidation Data'}
          </div>
          <LiquidationsView language={language} />
        </div>
      </div>
    </div>
    </PullToRefresh>
  );
}
